import {
    CountrySlugs,
    CountryVersion,
} from "@smile2impress/attraction-constants";
import { useEffect, useState } from "react";
import { mergeDeepRight } from "ramda";
import { useRouter } from "next/router";
import { useCmsPageNavigation } from "context/cmsPageNavigationContext";
import { useUTSLocaleContext } from "context/utsLocaleContext";
import { useLocationContext } from "context/locationContext";
import { useLocationApiContext } from "context/locationApiContext";

import {
    getRedirectPathByRegion,
    getTranslatedRegions,
    setPreferredCityCookie,
    setPreferredRegionCookie,
} from "components/Header/components/LocationSelect/helpers";
import {
    CityType,
    RegionType,
} from "components/Header/components/LocationSelect";
import { isServer } from "utils/next";
import { getCookiesValueByName } from "utils/cookie";
import { UrlsConfig } from "configs/urlsConfig";
import { COOKIES, CountryVersionToLocale } from "constants/enum";

export const useLocationSelect = () => {
    const { locale } = useUTSLocaleContext();
    const { hreflangAlternates } = useCmsPageNavigation();
    const router = useRouter();
    const { city, version } = useLocationContext();
    const regions = getTranslatedRegions(version);
    const { onChangeCity } = useLocationApiContext();
    const [activeRegion, updateActiveRegion] =
        useState<CountryVersion>(version);
    const getRegionByVersion = () =>
        regions.find((region) => region.id === version)?.name || "";
    const [displayedLocation, setDisplayedLocation] = useState<string>(
        city?.name || getRegionByVersion()
    );

    const [isVisible, setVisibility] = useState(false);
    const [initialStep, setInitialStep] = useState<number>(2);

    useEffect(() => {
        setDisplayedLocation(city?.name || getRegionByVersion());
    }, [version]);

    const saveCityToHistoryState = (cityToSave = city) => {
        const { state } = history;
        history.replaceState(
            mergeDeepRight(state, {
                options: { city: cityToSave },
            }),
            ""
        );
    };

    useEffect(
        () => {
            const {
                state: { options },
            } = history;
            if (options) {
                const { city: cityFromOptions } = options;
                if (cityFromOptions) {
                    onChangeCity(cityFromOptions);
                }
                if (!cityFromOptions) saveCityToHistoryState();
            }
        },
        isServer() ? [] : [history?.state?.options?.city]
    );

    useEffect(() => {
        const cityWasDetectedFor = getCookiesValueByName(
            COOKIES.GEO_SEARCH_COUNTRY
        );
        if (cityWasDetectedFor === CountrySlugs[locale] && city?.name) {
            setDisplayedLocation(city.name);
        }
    }, []);

    const onSelectCity = (newCity: CityType) => {
        setVisibility(false);
        setInitialStep(2);
        const id = parseInt(newCity.id, 10);
        const name = newCity.name || newCity.value;
        setPreferredRegionCookie(activeRegion);
        setPreferredCityCookie(newCity.id, name, activeRegion);
        onChangeCity({ id, name });
        setDisplayedLocation(name);

        if (version !== activeRegion) {
            const nextLocale = CountryVersionToLocale[activeRegion];
            const nextPath = getRedirectPathByRegion(
                router.pathname as UrlsConfig,
                nextLocale,
                { hreflangAlternates }
            );
            router.push(nextPath, "", { locale: nextLocale });
        } else {
            saveCityToHistoryState({ id, name });
        }
    };

    const onSelectRegion = (region: RegionType) => {
        setPreferredRegionCookie(region.id);
        updateActiveRegion(region.id);

        setVisibility(false);
        setInitialStep(2);
        setDisplayedLocation(region?.name as string);

        if (version !== region.id) {
            const nextLocale = CountryVersionToLocale[region.id];
            const nextPath = getRedirectPathByRegion(
                router.pathname as UrlsConfig,
                nextLocale,
                { hreflangAlternates }
            );
            router.push(nextPath, "", { locale: nextLocale });
        }
    };

    const [isOpenInMainMenu, setIsOpenInMainMenu] = useState(false);

    return {
        displayedLocation,
        isVisible,
        initialStep,
        isOpenInMainMenu,
        setIsOpenInMainMenu,
        onSelectCity,
        onSelectRegion,
        setInitialStep,
        setDisplayedLocation,
        setVisibility,
        city,
        version,
    };
};

export type UseLocationSelectReturn = ReturnType<typeof useLocationSelect>;
